import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Star } from "lucide-react";

const testimonialSchema = z.object({
  name: z.string().min(2, "Name is required"),
  role: z.string().min(2, "Role is required"),
  company: z.string().min(2, "Company is required"),
  avatar: z.string().optional().nullable(),
  quote: z.string().min(10, "Quote must be at least 10 characters"),
  rating: z.number().min(1).max(5).default(5),
  service: z.string().optional().nullable(),
  published: z.boolean().default(true),
  order: z.number().default(0),
});

type TestimonialFormValues = z.infer<typeof testimonialSchema>;

interface TestimonialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testimonial?: any;
}

export function TestimonialDialog({ open, onOpenChange, testimonial }: TestimonialDialogProps) {
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(5);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<TestimonialFormValues>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: {
      name: "",
      role: "",
      company: "",
      avatar: "",
      quote: "",
      rating: 5,
      service: "",
      published: true,
      order: 0,
    }
  });

  const published = watch("published");

  useEffect(() => {
    if (testimonial) {
      reset({
        name: testimonial.name,
        role: testimonial.role,
        company: testimonial.company,
        avatar: testimonial.avatar || "",
        quote: testimonial.quote,
        rating: testimonial.rating || 5,
        service: testimonial.service || "",
        published: testimonial.published ?? true,
        order: testimonial.order || 0,
      });
      setRating(testimonial.rating || 5);
    } else {
      reset({
        name: "",
        role: "",
        company: "",
        avatar: "",
        quote: "",
        rating: 5,
        service: "",
        published: true,
        order: 0,
      });
      setRating(5);
    }
  }, [testimonial, reset]);

  const mutation = useMutation({
    mutationFn: async (values: TestimonialFormValues) => {
      const url = testimonial 
        ? `/api/testimonials/${testimonial.id}/update` 
        : "/api/testimonials";
      const method = testimonial ? "PUT" : "POST";
      
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      
      if (!res.ok) throw new Error("Failed to save testimonial");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testimonials"] });
      onOpenChange(false);
      reset();
    }
  });

  const onSubmit = (values: TestimonialFormValues) => {
    mutation.mutate(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 flex flex-col max-h-[90vh]">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>{testimonial ? "Edit Testimonial" : "Add Testimonial"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Client Name</Label>
                <Input id="name" {...register("name")} placeholder="e.g. John Doe" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input id="company" {...register("company")} placeholder="e.g. TechCorp" />
                {errors.company && <p className="text-xs text-destructive">{errors.company.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input id="role" {...register("role")} placeholder="e.g. CTO" />
                {errors.role && <p className="text-xs text-destructive">{errors.role.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="service">Service Provided</Label>
                <Input id="service" {...register("service")} placeholder="e.g. Web Development" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="avatar">Avatar URL (Optional)</Label>
              <Input id="avatar" {...register("avatar")} placeholder="https://..." />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quote">Testimonial Quote</Label>
              <Textarea
                id="quote"
                {...register("quote")}
                placeholder="What did the client say?"
                className="min-h-[100px]"
              />
              {errors.quote && <p className="text-xs text-destructive">{errors.quote.message}</p>}
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Label>Rating</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setRating(s);
                        setValue("rating", s);
                      }}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star
                        className={`h-6 w-6 ${s <= rating ? "fill-yellow-500 text-yellow-500" : "text-muted"}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="published"
                  checked={published}
                  onCheckedChange={(checked) => setValue("published", checked)}
                />
                <Label htmlFor="published">Published</Label>
              </div>
            </div>
          </div>

          <div className="p-6 pt-4 border-t border-border/10 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : (testimonial ? "Save Changes" : "Create Testimonial")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
